import React, { Component } from 'react';
import compareVersions from 'compare-versions';
import { StyleSheet, View, TouchableOpacity, Keyboard, TextInput, ScrollView } from 'react-native';
import i18next from 'i18next';
import shortid from 'shortid';
import moment from 'moment';

import SignUpModal from './SignUpModal';
import ForgotPasswordModal from './ForgotPasswordModal';

import Button from './shared/Button';
import Text from './shared/Text';
import Image from './shared/Image';
import { TextInputLabel } from './shared/TextInput';

import authStore, { LOGGED_IN, InitDataType } from '../stores/auth';
import appStore from '../stores/app';
import heroStore from '../stores/hero';

import log from '../lib/log';

import { signUp, uniqLogin } from '../lib/userApiCalls';

import {
  TOUCHABLE,
  PADDING,
  PADDING1,
  WINDOW_HEIGHT,
  BASE_COLOR,
  SECONDARY_COLOR,
  BACKGROUND_COLOR,
  WHITE_COLOR,
  MARGIN_10,
  MARGIN_15,
  pOrT,
} from './styleConstants';
import { VERSION, IS_IOS, IS_WEB, QUICK_PASSWORD } from '../lib/appConstants';

import { APP_STORE_LINK, PLAY_STORE_LINK, PATREON_LINK } from '../lib/config';

import { t, getLng, getFromStorage, setToStorage, linkTo } from '../utils/appUtils';

import { getMenuLinks, renderLinks } from './common/Footer';
import { generateWarriorName } from '../../data/utils';

export const COPYRIGHT_TEXT = `COPYRIGHT ${'\u00A9'} ${new Date().getFullYear()} BY BUTUZGOL`;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  baseText: {
    fontSize: pOrT(10, 12),
    color: WHITE_COLOR,
  },
  forgotPasswordBtn: {
    alignSelf: 'flex-end',
    zIndex: 99,
    marginTop: -20,
    marginRight: 5,
    height: 20,
  },
  baseSecondText: {
    fontSize: pOrT(10, 12),
    color: SECONDARY_COLOR,
  },
  dividerWrapper: {
    paddingHorizontal: 30,
    width: 30,
  },
  dividerText: {
    position: 'absolute',
    top: 40,
    alignSelf: 'center',
    paddingVertical: 10,
    backgroundColor: BACKGROUND_COLOR,
    zIndex: 2,
    fontSize: 18,
    color: BASE_COLOR,
  },
  bottomLabelWrapper: {
    alignSelf: 'center',
    flexDirection: 'row',
    marginTop: MARGIN_15,
  },
  bottomLabelButton: {
    marginLeft: MARGIN_10,
  },
  bottomLabelButtonText: {
    color: SECONDARY_COLOR,
  },
  form: {
    marginTop: pOrT(-20, -100),
    alignItems: 'center',
  },
  lng: {
    flexDirection: 'row',
    marginTop: PADDING,
  },
  copyright: {
    position: 'absolute',
    bottom: 15,
  },
  copyrightText: {
    marginTop: PADDING1,
  },
  patreon: {
    backgroundColor: '#ff424d',
    paddingHorizontal: 13,
    paddingVertical: 6,
    display: 'flex',
    flexDirection: 'row',
  },
});

async function postSignIn(firstTime?: boolean) {
  appStore.toggleLoading(false);

  if (!IS_WEB && compareVersions(VERSION, appStore.config.latestSupportedVersion!) === -1) {
    // updateAppRequired();
    // return;
  }

  if (!heroStore.hero) {
    appStore.showMessage({ type: 'error', text: t('auth.heroNotFound') });
    return;
  }

  if (firstTime) {
    appStore.navigate('Intro', 'outer', 'reset');
  } else {
    appStore.navigate('Inner', 'outer', 'reset');
  }
}

const init = async () => {
  if (authStore.isLoggedIn) {
    appStore.clear();
    appStore.toggleLoading(true);
    // Delay i need might for navigation
    setTimeout(async () => {
      await postSignIn();
    }, 0);
    return;
  }

  if (!(await getFromStorage(LOGGED_IN))) return;

  appStore.toggleLoading(true);
  const result = await authStore.init();
  if (result === false) {
    appStore.toggleLoading(false);
    return;
  }

  if (result) {
    appStore.showMessage({ type: 'error', text: result });
    appStore.toggleLoading(false);
    return;
  }
  await postSignIn();
};

async function signIn(email: string, password: string, data?: InitDataType) {
  if (!email || !password) return;

  Keyboard.dismiss();
  appStore.toggleLoading(true);
  const result = await authStore.signIn(email, password, data);

  if (result) {
    appStore.showMessage({ type: 'error', text: result });
    appStore.toggleLoading(false);
    return;
  }

  await postSignIn(Boolean(data));
}

type BottomLabelProps = {
  leftText: string;
  rightText: string;
  onPress: (arg?: any) => void;
  testID?: string;
};

export const BottomLabel = ({ leftText, rightText, onPress, testID }: BottomLabelProps) => (
  <View style={styles.bottomLabelWrapper}>
    <Text bold style={[styles.baseText, { color: BASE_COLOR }]}>
      {leftText.toUpperCase()}
    </Text>
    <TouchableOpacity
      hitSlop={{
        top: TOUCHABLE,
        bottom: TOUCHABLE,
        left: TOUCHABLE,
        right: TOUCHABLE,
      }}
      onPress={onPress}
      style={styles.bottomLabelButton}
      testID={testID}
    >
      <Text bold style={[styles.baseText, styles.bottomLabelButtonText]}>
        {rightText.toUpperCase()}
      </Text>
    </TouchableOpacity>
  </View>
);

async function generateQuickUser(): Promise<{
  email: string;
  password: string;
  data: { login: string };
}> {
  let login: string;
  let uniqLoginObj: any;
  do {
    login = generateWarriorName();
    uniqLoginObj = await uniqLogin(login);
  } while (uniqLoginObj);

  return {
    email: `play${shortid()}@sf.com`,
    password: QUICK_PASSWORD,
    data: {
      login,
    },
  };
}

type Props = {};

type State = {
  fields: {
    email: string;
    password: string;
  };
  showSignUpModal: boolean;
  showForgotPasswordModal: boolean;
};

export default class SignInScreen extends Component<Props, State> {
  state = {
    fields: {
      email: '',
      password: '',
    },
    showSignUpModal: false,
    showForgotPasswordModal: false,
  };

  signUpAfterFB = false;

  inputs: { [key: string]: TextInput } = {};

  constructor(props: Props) {
    super(props);
    init();
  }

  onChangeInput(key: string, value: string) {
    this.setState((state) => ({
      fields: {
        ...state.fields,
        [key]: value,
      },
    }));
  }

  onSubmit = () => {
    const { fields } = this.state;
    let { email } = fields;
    email = email.trim();
    signIn(email, fields.password);
    (log as any)('SignInAction', { email });
  };

  onSignInFb = async (data: any) => {
    appStore.toggleLoading(true);

    const result = await authStore.signInFB(data);
    if (result === false) {
      appStore.toggleLoading(false);
      return;
    }

    if (result && result.message) {
      appStore.toggleLoading(false);
      appStore.showMessage({ type: 'error', text: result.message });
      return;
    }

    if (result && result.signUp) {
      appStore.toggleLoading(false);
      this.toggleSignUp(true, true);
    } else {
      await postSignIn(Boolean(data));
    }
  };

  onPlay = async () => {
    (log as any)('QuickPlay');
    appStore.toggleLoading(true);

    let user: any = {};
    let pass = true;
    do {
      try {
        user = await generateQuickUser();
        await signUp(user.email, user.password);
        pass = true;
      } catch (e) {
        pass = false;
      }
    } while (!pass);

    signIn(user.email, user.password, { ...user.data, quick: true });
  };

  onChangeLng(lng: string) {
    i18next.changeLanguage(lng, async (err: any) => {
      setToStorage('lng', lng);
      moment.locale(lng);
      this.forceUpdate();
    });
  }

  toggleSignUp(value: boolean, isAfterFB: boolean = false) {
    this.signUpAfterFB = isAfterFB;
    this.setState({
      showSignUpModal: value,
    });
  }

  focusNextField(id: string) {
    (this.inputs as any)[id]?.focus();
  }

  render() {
    const { fields, showSignUpModal, showForgotPasswordModal } = this.state;

    const links = getMenuLinks(false);
    const lng = getLng();

    return (
      <View testID="signInScreen" style={styles.container}>
        <View style={styles.form}>
          <Image
            source={require('../assets/images/logo.png')}
            style={pOrT(
              {
                position: 'absolute',
                top: -(((WINDOW_HEIGHT - 225) / 2 - 60 - 20 + 10) / 2 + 20 + 60),
                width: 36,
                height: 60,
                zIndex: 999,
              },
              { position: 'absolute', top: -180, width: 84, height: 140 },
            )}
          />
          <View
            style={{
              flexDirection: 'row',
            }}
          >
            <Image
              style={{
                position: 'absolute',
                width: 540,
                height: 225,
                marginLeft: -20,
                marginTop: -20,
              }}
              resizeMode="stretch"
              source={require('../assets/images/paper3.png')}
            />
            <View>
              <Button onPress={this.onPlay} size="large" testID="signIn.quickPlay">
                {t('auth.play')}
              </Button>

              <Button
                style={{ marginTop: MARGIN_10 }}
                onPress={() => this.toggleSignUp(true)}
                size="large"
                variant="red"
                testID="signIn.signUpForm"
              >
                {t('auth.signUpLink')}
              </Button>

              {/* <Button
                style={{ marginTop: MARGIN_10 }}
                onPress={this.onSignInFb}
                size="large">
                {t('auth.signInWithFB')}
              </Button> */}
            </View>
            <View style={styles.dividerWrapper}>
              <Text bold style={[styles.baseText, styles.dividerText]}>
                {t('auth.or').toUpperCase()}
              </Text>
            </View>
            <View>
              <TextInputLabel
                label={t('auth.email').toUpperCase()}
                value={fields.email}
                autoCapitalize="none"
                returnKeyType="next"
                onSubmitEditing={() => {
                  this.focusNextField('password');
                }}
                onChangeText={(value: string) => this.onChangeInput('email', value)}
                testID="signIn.fields.email"
              />

              <TextInputLabel
                label={t('auth.password').toUpperCase()}
                secureTextEntry
                style={{ marginTop: MARGIN_10 }}
                value={fields.password}
                inputStyle={{
                  paddingRight: !IS_WEB ? 110 : undefined,
                  width: IS_WEB ? 90 : undefined,
                }}
                autoCapitalize="none"
                inputRef={(ref: any) => {
                  (this.inputs as any).password = ref;
                }}
                returnKeyType="done"
                onSubmitEditing={this.onSubmit}
                onChangeText={(value: string) => this.onChangeInput('password', value)}
                testID="signIn.fields.password"
              />

              <TouchableOpacity
                onPress={() => {
                  this.setState({
                    showForgotPasswordModal: true,
                  });
                }}
                hitSlop={{
                  top: 10,
                  bottom: TOUCHABLE,
                  left: TOUCHABLE,
                  right: TOUCHABLE,
                }}
                style={styles.forgotPasswordBtn}
              >
                <Text bold style={styles.baseSecondText}>
                  {t('auth.forgotPassword')}
                </Text>
              </TouchableOpacity>

              <Button
                style={{ marginTop: MARGIN_10 }}
                onPress={this.onSubmit}
                size="large"
                testID="signIn.submit"
              >
                {t('auth.signIn')}
              </Button>
            </View>
          </View>
        </View>

        {!IS_IOS ? (
          <View
            style={{
              position: 'absolute',
              right: 0,
              bottom: 10,
            }}
          >
            <TouchableOpacity
              style={styles.patreon}
              onPress={() => {
                linkTo(PATREON_LINK);
              }}
            >
              <Text bold style={{ color: 'black', fontSize: 13 }}>
                BECOME A PATRON
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}

        <View style={[styles.copyright, { bottom: 50, right: 0 }]}>
          <View
            style={{
              flexDirection: 'row',
            }}
          >
            {renderLinks(links)}
          </View>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[
            styles.lng,
            {
              position: 'absolute',
              left: 0,
              bottom: 35,
              zIndex: 2,
              maxWidth: 300,
            },
          ]}
        >
          {[
            { key: 'en', label: t('english') },
            { key: 'es', label: t('spanish') },
            { key: 'pt', label: t('portuguese') },
            { key: 'zh', label: t('chinese') },
            { key: 'ja', label: t('japanese') },
            { key: 'ko', label: t('korean') },
            { key: 'ru', label: t('russian') },
            { key: 'fr', label: t('french') },
            { key: 'de', label: t('german') },
            { key: 'it', label: t('italian') },
          ].map((item) => (
            <TouchableOpacity
              key={item.key}
              style={{ marginRight: 10 }}
              onPress={() => this.onChangeLng(item.key)}
            >
              <Text bold={lng === item.key} style={styles.baseSecondText}>
                {item.label.toUpperCase()}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        <View style={[styles.copyright, { left: 0 }]}>
          <Text style={[styles.baseText, styles.copyrightText]}>
            {COPYRIGHT_TEXT} V {VERSION}
          </Text>
        </View>

        {IS_WEB ? (
          <View style={{ position: 'absolute', left: 0, bottom: 110 }}>
            {React.createElement('div', {
              dangerouslySetInnerHTML: {
                __html: `
                <a target="_blank" href="${APP_STORE_LINK}" style="display:inline-block;overflow:hidden;background:url(https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/${
                  lng === 'en'
                    ? 'en-us'
                    : lng === 'ru'
                    ? 'ru-ru'
                    : lng === 'es'
                    ? 'es-es'
                    : lng === 'it'
                    ? 'it-it'
                    : lng === 'fr'
                    ? 'fr-fr'
                    : lng === 'de'
                    ? 'de-de'
                    : lng === 'pt'
                    ? 'pt-pt'
                    : lng === 'zh'
                    ? 'zh-cn'
                    : lng === 'ja'
                    ? 'ja-jp'
                    : lng === 'ko'
                    ? 'ko-kr'
                    : 'en-us'
                }?size=250x83&amp;releaseDate=1530662400) no-repeat;width:135px;height:40px;background-size:contain;" />`,
              },
            })}
            {React.createElement('div', {
              style: { marginTop: 5 },
              dangerouslySetInnerHTML: {
                __html: `<a target="_blank" href="${PLAY_STORE_LINK}" style="display: inline-block; width: 135px; overflow: hidden; height: 40px;"><img alt='Get it on Google Play' src="https://play.google.com/intl/en_us/badges/images/generic/${
                  lng === 'zh' ? 'zh-cn' : lng
                }_badge_web_generic.png" style="width: 155px; margin-left: -10px; margin-top: -10px; display: inline-block;"/></a>`,
              },
            })}
          </View>
        ) : null}

        {showSignUpModal ? (
          <SignUpModal
            isFB={this.signUpAfterFB}
            onHide={(data: any) => {
              const afterFB = this.signUpAfterFB;
              this.toggleSignUp(false);

              if (data) {
                if (!afterFB) signIn(data.email, data.password, data.data);
                else this.onSignInFb(data.data);
              }
            }}
          />
        ) : null}
        {showForgotPasswordModal ? (
          <ForgotPasswordModal
            onHide={() => {
              this.setState({
                showForgotPasswordModal: false,
              });
            }}
          />
        ) : null}
      </View>
    );
  }
}
